import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { Button, ButtonIcon, ButtonLabel } from 'primeng/button';
import { InputText } from 'primeng/inputtext';

import { TranslationModule } from '@core/translation/translation.module';
import { U2bIconButtonComponent } from '@u2bUi/icon-button/u2b-icon-button.component';

import { BookingV2Facade } from '../../../services/booking-v2.facade';

@Component({
    selector: 'booking-v2-travelers-tab',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, Button, ButtonIcon, ButtonLabel, InputText, TranslationModule, U2bIconButtonComponent],
    host: { class: 'block' },
    template: `
        <div class="flex items-center justify-between gap-3 mb-4">
            <p class="text-sm text-p-text-muted m-0">
                @if (mainPersonName(); as name) {
                    {{ 'bookingV2.advanced.travelers.mainPersonHint' | translate: { name } }}
                }
            </p>
            @if (!readOnly()) {
                <p-button type="button" (onClick)="toggleForm()">
                    <span pButtonIcon class="material-icons" style="font-size: 1.125rem">add</span>
                    <span pButtonLabel>{{ 'bookingV2.advanced.travelers.addButton' | translate }}</span>
                </p-button>
            }
        </div>

        @if (showForm()) {
            <div class="bg-p-surface-50 border border-p-border rounded-lg p-5 mb-4 flex flex-col gap-3">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div class="flex flex-col gap-1">
                        <label for="t-first" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.advanced.travelers.firstName' | translate }}
                        </label>
                        <input pInputText id="t-first" type="text" class="w-full"
                            [ngModel]="firstName()" (ngModelChange)="firstName.set($event)" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label for="t-last" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.advanced.travelers.lastName' | translate }}
                        </label>
                        <input pInputText id="t-last" type="text" class="w-full"
                            [ngModel]="lastName()" (ngModelChange)="lastName.set($event)" />
                    </div>
                </div>
                <div class="flex gap-2 justify-end">
                    <p-button type="button" [disabled]="!canAdd()" [label]="'common.save' | translate" (onClick)="addTraveler()" />
                    <p-button type="button" severity="secondary" [text]="true" [label]="'common.cancel' | translate" (onClick)="closeForm()" />
                </div>
            </div>
        }

        <div class="flex flex-col gap-2">
            @if (mainPersonName(); as name) {
                <div class="flex items-center justify-between gap-3 rounded-lg border px-3 py-3"
                     style="background: color-mix(in srgb, #0284c7 6%, transparent); border-color: color-mix(in srgb, #0284c7 25%, transparent);">
                    <span class="font-semibold text-p-text">{{ name }}</span>
                    <span class="px-2 py-0.5 bg-teal-700 text-white text-xs rounded-full">
                        {{ 'bookingV2.advanced.travelers.mainBadge' | translate }}
                    </span>
                </div>
            }
            @for (traveler of travelers(); track $index) {
                <div class="flex items-center justify-between gap-3 bg-p-content-bg border border-p-border rounded-lg px-3 py-3">
                    <span class="text-p-text">{{ traveler.firstName }} {{ traveler.lastName }}</span>
                    @if (!readOnly()) {
                        <u2b-icon-button
                            icon="close"
                            [ariaLabel]="('bookingV2.advanced.travelers.removeAria' | translate)"
                            (clicked)="removeTraveler($index)" />
                    }
                </div>
            }
            @if (travelers().length === 0 && !showForm()) {
                <p class="text-center text-p-text-muted py-6 text-sm m-0">
                    {{ 'bookingV2.advanced.travelers.empty' | translate }}
                </p>
            }
        </div>
    `,
})
export class TravelersTabComponent {

    private readonly facade = inject(BookingV2Facade);

    readonly readOnly = input<boolean>(false);

    constructor() {
        // Mark the advanced step dirty while the add form is open & unsaved, so
        // navigating away (tab/step/dialog) prompts to discard.
        effect(() => this.facade.advancedDraftDirty.set(!this.readOnly() && this.showForm()));
    }

    readonly travelers = this.facade.travelers;

    readonly mainPersonName = computed(() => this.facade.selectedPerson()?.displayName ?? null);

    readonly showForm = signal<boolean>(false);
    readonly firstName = signal<string>('');
    readonly lastName = signal<string>('');

    readonly canAdd = computed(() =>
        this.firstName().trim().length > 0 && this.lastName().trim().length > 0,
    );

    toggleForm(): void {
        this.showForm.update(v => !v);
    }

    closeForm(): void {
        this.showForm.set(false);
        this.firstName.set('');
        this.lastName.set('');
    }

    addTraveler(): void {
        if (!this.canAdd()) {
            return;
        }
        this.facade.travelers.update(list => [
            ...list,
            { firstName: this.firstName().trim(), lastName: this.lastName().trim() },
        ]);
        this.closeForm();
    }

    removeTraveler(index: number): void {
        this.facade.travelers.update(list => list.filter((_, i) => i !== index));
    }
}
